/**
 * Mov watch-party wire protocol — the single source of truth shared by the
 * browser client (src/) and the Cloudflare Worker / Durable Object (worker/).
 *
 * Design principle (see README "Sync protocol"): control events are ABSOLUTE.
 * We never send "pause now". We send "the video should be at position P at
 * shared-clock time T, playing/paused". Receivers project to where the video
 * *should be right now* and steer toward it, which makes lateness, reordering
 * and duplicate delivery self-correcting.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Playback state (the authoritative room state, held by the Durable Object and
// mirrored by each client)
// ─────────────────────────────────────────────────────────────────────────────

export interface PlaybackState {
  /** Logical clock for control events. Bumps by 1 on every user control. */
  epoch: number;
  /** Video position (seconds) that is valid at `refSharedMs`. */
  positionS: number;
  /** Shared-clock time (ms) that `positionS` is valid for. */
  refSharedMs: number;
  /** true = paused, false = playing. */
  paused: boolean;
  /** Nominal playback speed (0.5..2). 1 = normal. Follower rate-nudging is separate. */
  rate: number;
  /** clientId that issued the control producing this state. */
  originId: string;
}

export function initialState(nowSharedMs: number): PlaybackState {
  return {
    epoch: 0,
    positionS: 0,
    refSharedMs: nowSharedMs,
    paused: true,
    rate: 1,
    originId: "",
  };
}

export type ControlAction = "play" | "pause" | "seek" | "rate";

// ─────────────────────────────────────────────────────────────────────────────
// Tunable constants (starting values — see README parameter cheat-sheet)
// ─────────────────────────────────────────────────────────────────────────────

export const TIMING = {
  /** Clock offset: burst of pings at connect. */
  CLOCK_BURST_COUNT: 8,
  CLOCK_BURST_SPACING_MS: 200,
  /** Steady re-sync ping cadence. */
  CLOCK_STEADY_MS: 5000,
  /** Sliding window of clock samples to keep (min-RTT is chosen from these). */
  CLOCK_WINDOW: 16,
  /** Max per-update step when easing the offset toward a new best (anti-teleport). */
  CLOCK_EMA_MAX_STEP_MS: 20,

  /** Heartbeat cadence (health + buffer coordination). */
  HEARTBEAT_MS: 1000,
  /** Drift correction evaluation cadence. */
  DRIFT_EVAL_MS: 250,

  /** Buffer barrier: consider stalled if no progress for this long while playing. */
  STALL_NO_PROGRESS_MS: 400,
  /** Buffering must be clear this long before we arm a resume (anti-thrash). */
  BUFFER_CLEAR_DEBOUNCE_MS: 250,
  /** Lead time for the scheduled synchronized resume. */
  RESUME_DELAY_MS: 400,
  /** Failsafe: if a non-reference client has been clear this long without a
   * resume_at from the reference, it self-resumes so it can never get stuck. */
  BARRIER_FAILSAFE_MS: 700,
  /** If a client buffers longer than this, surface a skip/lower-quality prompt. */
  BUFFER_WATCHDOG_MS: 20000,

  /** After a user seek, suppress drift correction this long to let the element settle. */
  SEEK_SETTLE_MS: 500,
  /** After applying a remote event, keep the broadcast-suppression flag up this long. */
  SUPPRESS_BROADCAST_MS: 60,
} as const;

export const DRIFT = {
  /** Dead-band floor (seconds). Real floor = max(this, rttHalf + margin). */
  SOFT_FLOOR_S: 0.05,
  SOFT_MARGIN_S: 0.03,
  /** Above this absolute error we hard-seek instead of nudging (seconds). */
  HARD_S: 2.0,
  /** Consecutive over-HARD samples required before hard-seeking (hysteresis). */
  HARD_CONSECUTIVE: 2,
  /** Proportional nudge time-constant (seconds). rate = clamp(1 - err/TAU, ...). */
  TAU_S: 4.0,
  /** Asymmetric rate clamps: speed up (behind) more freely than slow down (ahead),
   * because humans notice slow-motion / pitch-down more than a mild speed-up. */
  RATE_MIN: 0.95, // slow-down floor (ahead) — gentle
  RATE_MAX: 1.1, // speed-up ceiling (behind) — more headroom
} as const;

export const LIMITS = {
  MIN_RATE: 0.5,
  MAX_RATE: 2.0,
  /** Presigned upload URL lifetime (seconds). */
  UPLOAD_URL_TTL_S: 3600,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Wire messages
// ─────────────────────────────────────────────────────────────────────────────

/** Clock sync — client asks, server stamps its own clock on receive & send. */
export interface ClockPing {
  t: "clock_ping";
  id: string;
  cT1: number; // client monotonic ms at send
}
export interface ClockPong {
  t: "clock_pong";
  id: string;
  cT1: number; // echoed
  sT2: number; // server ms at receive
  sT3: number; // server ms at send
}

/** A user-initiated control. Carries absolute (positionS @ atSharedMs). */
export interface ControlMsg {
  t: "control";
  action: ControlAction;
  epoch: number;
  originId: string;
  positionS: number;
  atSharedMs: number;
  rate: number;
  paused: boolean;
  msgId: string;
}

/** Periodic health + buffer telemetry. NOT the primary drift signal. */
export interface HeartbeatMsg {
  t: "heartbeat";
  originId: string;
  epoch: number;
  videoTimeS: number;
  atSharedMs: number;
  paused: boolean;
  buffering: boolean;
  readyState: number; // HTMLMediaElement.readyState 0..4
}

/** Edge-triggered buffer state change (sent immediately, not on heartbeat tick). */
export interface BufferStateMsg {
  t: "buffer_state";
  originId: string;
  buffering: boolean;
  atVideoTimeS: number;
  atSharedMs: number;
}

/** Reference client schedules a synchronized resume out of the buffer barrier. */
export interface ResumeAtMsg {
  t: "resume_at";
  epoch: number;
  originId: string;
  positionS: number;
  atSharedMs: number;
}

/** Client's opening frame after socket connect. */
export interface HelloMsg {
  t: "hello";
  clientId: string;
  /** Optional human label for presence UI. */
  name?: string;
}

/** Server → client on join: identity, role, clock seed, current room state. */
export interface WelcomeMsg {
  t: "welcome";
  clientId: string;
  isReference: boolean;
  serverTimeMs: number;
  snapshot: PlaybackState;
  peerConnected: boolean;
}

/** Server → client when the room's authoritative state changes / on request. */
export interface SnapshotMsg {
  t: "snapshot";
  state: PlaybackState;
}

/** Server → client presence changes. `isReference` may flip on peer leave. */
export interface PresenceMsg {
  t: "presence";
  peerConnected: boolean;
  isReference: boolean;
}

export type ClientToServer =
  | HelloMsg
  | ClockPing
  | ControlMsg
  | HeartbeatMsg
  | BufferStateMsg
  | ResumeAtMsg;

export type ServerToClient =
  | WelcomeMsg
  | ClockPong
  | SnapshotMsg
  | PresenceMsg
  | ControlMsg
  | HeartbeatMsg
  | BufferStateMsg
  | ResumeAtMsg;

export type AnyMessage = ClientToServer | ServerToClient;

/** Narrowing helper used on both ends. */
export function isMessage(v: unknown): v is AnyMessage {
  return typeof v === "object" && v !== null && typeof (v as { t?: unknown }).t === "string";
}

// ─────────────────────────────────────────────────────────────────────────────
// HTTP API (Worker) — request/response shapes for uploads
// ─────────────────────────────────────────────────────────────────────────────

export interface UploadUrlRequest {
  /** e.g. "movies/<id>/stream_0/segment_001.ts" */
  key: string;
  contentType?: string;
}
export interface UploadUrlResponse {
  url: string;
  method: "PUT";
  expiresInS: number;
}
